import React from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Checkbox } from 'js/design_system/forms/Checkbox';
import { Input } from 'js/design_system/forms/Input';
import { TextButton } from 'js/design_system/TextButton';

import { emptyName, NameDraft } from './formState';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
`;

const Row = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;

  @include breakpoint(medium, large) {
    flex-direction: row;
    align-items: flex-start;
  }

  & > * {
    flex: 1;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-16, 16px);
  align-items: center;
`;

type NameListProps = {
  label: string;
  organizationLabel: string;
  names: NameDraft[];
  required: boolean;
  onChange: (names: NameDraft[]) => void;
};

export function NameList({
  label,
  organizationLabel,
  names,
  required,
  onChange,
}: NameListProps): React.ReactElement {
  const updateAt = (index: number, patch: Partial<NameDraft>): void => {
    onChange(
      names.map((name, nameIndex) =>
        nameIndex === index ? { ...name, ...patch } : name,
      ),
    );
  };

  const removeAt = (index: number): void => {
    if (names.length === 1) {
      onChange([emptyName()]);
      return;
    }
    onChange(names.filter((_, nameIndex) => nameIndex !== index));
  };

  return (
    <List>
      {names.map((name, index) => (
        <List key={`${label}-${index}`}>
          {name.isOrganization ? (
            <Input
              label={
                index === 0
                  ? `${label}${required ? ' *' : ''}`
                  : `${label} ${index + 1}`
              }
              value={name.literal}
              autoComplete="organization"
              onChange={(event) =>
                updateAt(index, { literal: event.target.value })
              }
            />
          ) : (
            <Row>
              <Input
                label={
                  index === 0
                    ? `Family name${required ? ' *' : ''}`
                    : `Family name ${index + 1}`
                }
                value={name.family}
                autoComplete="family-name"
                onChange={(event) =>
                  updateAt(index, {
                    family: event.target.value,
                    needsVerification: false,
                  })
                }
              />
              <Input
                label={index === 0 ? 'Given name' : `Given name ${index + 1}`}
                value={name.given}
                autoComplete="given-name"
                onChange={(event) =>
                  updateAt(index, {
                    given: event.target.value,
                    needsVerification: false,
                  })
                }
              />
            </Row>
          )}
          <Actions>
            {name.needsVerification && (
              <Body size="sm">
                Check this name — it was split from a display name.
              </Body>
            )}
            <Checkbox
              label={organizationLabel}
              checked={name.isOrganization}
              onChange={(checked) =>
                updateAt(index, { isOrganization: checked })
              }
            />
            {names.length > 1 && (
              <TextButton
                type="button"
                size="sm"
                inline
                onClick={() => removeAt(index)}
              >
                Remove
              </TextButton>
            )}
          </Actions>
        </List>
      ))}
      <div>
        <Button
          type="button"
          size="sm"
          secondary
          icon="add"
          onClick={() => onChange(names.concat(emptyName()))}
        >
          Add {label.toLowerCase()}
        </Button>
      </div>
    </List>
  );
}
